/**
 * CreditUsage — the detailed usage statement on Settings → Subscription.
 *
 * Shows, per wallet (AI / WhatsApp), the newest-first credit ledger with each
 * row's breakdown lifted from metadata: for an AI turn, the model + tokens +
 * what it cost; for a WhatsApp send, the message kind + template. This is the
 * "where did my credits go?" surface — every debit and grant, in detail.
 *
 * Cursor-paginated via `billing.creditTransactions` with a "Load more" button.
 */
import { useState } from "react";
import { Sparkles, MessageSquare, ReceiptText } from "lucide-react";
import { trpc } from "../../../lib/trpc.ts";
import { FormCard } from "../form-card.tsx";

type Wallet = "ai" | "whatsapp";

interface CreditUsageProps {
  /** Show the AI tab — only meaningful when the Flint AI add-on is on. */
  showAi: boolean;
  /** Show the WhatsApp tab — only meaningful when the WhatsApp Business add-on is on. */
  showWhatsapp: boolean;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function rupeesFromPaise(paise: number): string {
  return `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

interface TxnDetail {
  model?: string | null;
  modelKey?: string;
  queryType?: string;
  inputTokens?: number;
  outputTokens?: number;
  costPaise?: number;
  surface?: string;
  sendKind?: string;
  templateKey?: string;
}

/** One-line, human breakdown of what a credit row actually was. */
function detailLine(wallet: Wallet, detail: TxnDetail | null): string | null {
  if (!detail) return null;
  if (wallet === "ai") {
    const parts: string[] = [];
    const model = detail.modelKey ?? detail.model;
    if (model) parts.push(model);
    if (detail.inputTokens != null && detail.outputTokens != null) {
      parts.push(`${detail.inputTokens.toLocaleString("en-IN")} → ${detail.outputTokens.toLocaleString("en-IN")} tokens`);
    }
    if (detail.costPaise != null) parts.push(`${rupeesFromPaise(detail.costPaise)} cost`);
    return parts.length > 0 ? parts.join(" · ") : null;
  }
  const parts: string[] = [];
  if (detail.sendKind) parts.push(detail.sendKind);
  if (detail.templateKey) parts.push(detail.templateKey);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function WalletStatement({ wallet }: { wallet: Wallet }) {
  const q = trpc.billing.creditTransactions.useInfiniteQuery(
    { wallet },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );
  const rows = q.data?.pages.flatMap((p) => p.rows) ?? [];

  if (q.isLoading) {
    return <p className="px-5 py-8 text-center text-sm text-muted">Loading usage…</p>;
  }
  if (q.error) {
    return <p className="px-5 py-8 text-center text-sm text-bad">Couldn't load usage history.</p>;
  }
  if (rows.length === 0) {
    return (
      <div className="px-5 py-10 text-center">
        <ReceiptText size={24} className="mx-auto text-line-2" aria-hidden />
        <p className="mt-2 text-sm font-medium text-ink">No usage yet</p>
        <p className="mt-0.5 text-xs text-muted">
          Every credit spent or granted will show up here with its full breakdown.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-5 py-2.5 font-medium">When</th>
              <th className="px-5 py-2.5 font-medium">Activity</th>
              <th className="px-5 py-2.5 text-right font-medium">Credits</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((r) => {
              const line = detailLine(wallet, r.detail as TxnDetail | null);
              const spent = r.credits < 0;
              return (
                <tr key={r.id} className="hover:bg-bg/50">
                  <td className="whitespace-nowrap px-5 py-3 align-top text-ink-2">{formatWhen(r.createdAt)}</td>
                  <td className="px-5 py-3 align-top">
                    <p className="font-medium text-ink">{r.description}</p>
                    {line ? <p className="mt-0.5 text-xs text-muted">{line}</p> : null}
                  </td>
                  <td
                    className={`px-5 py-3 text-right align-top tabular-nums font-medium ${spent ? "text-ink" : "text-ok"}`}
                  >
                    {spent ? "" : "+"}
                    {r.credits.toLocaleString("en-IN")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {q.hasNextPage ? (
        <div className="border-t border-line px-5 py-3 text-center">
          <button
            type="button"
            onClick={() => void q.fetchNextPage()}
            disabled={q.isFetchingNextPage}
            className="text-xs font-medium text-sky-700 hover:underline disabled:opacity-60"
          >
            {q.isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}
    </>
  );
}

export function CreditUsage({ showAi, showWhatsapp }: CreditUsageProps) {
  // No credit wallets enabled — nothing to show.
  if (!showAi && !showWhatsapp) return null;

  const defaultWallet: Wallet = showAi ? "ai" : "whatsapp";
  const [wallet, setWallet] = useState<Wallet>(defaultWallet);

  const tab = (w: Wallet, label: string, Icon: typeof Sparkles) => (
    <button
      type="button"
      onClick={() => setWallet(w)}
      className={`inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-medium ${
        wallet === w ? "bg-ink text-paper" : "text-ink-2 hover:bg-bg"
      }`}
    >
      <Icon size={13} strokeWidth={2} aria-hidden />
      {label}
    </button>
  );

  return (
    <FormCard
      icon={<ReceiptText size={14} strokeWidth={2} aria-hidden />}
      title="Usage history"
      description="Every credit spent or granted, with what it was used for."
      badge={
        <div className="flex items-center gap-1">
          {showAi ? tab("ai", "AI", Sparkles) : null}
          {showWhatsapp ? tab("whatsapp", "WhatsApp", MessageSquare) : null}
        </div>
      }
      bodyClassName="p-0"
    >
      <WalletStatement key={wallet} wallet={wallet} />
    </FormCard>
  );
}
